import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  IconButton,
  Link as MuiLink,
  Paper,
  Stack,
  Step,
  StepConnector,
  StepLabel,
  Stepper,
  Tooltip,
  Typography,
  stepConnectorClasses,
} from '@mui/material'
import { styled } from '@mui/material/styles'
import CopyIcon from '../../common/CopyIcon'
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew'
import RefreshIcon from '@mui/icons-material/Refresh'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined'
import AudiotrackOutlinedIcon from '@mui/icons-material/AudiotrackOutlined'
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined'
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined'
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'
import HistoryEduOutlinedIcon from '@mui/icons-material/HistoryEduOutlined'
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, TextField as MuiTextField,
} from '@mui/material'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined'
import AssignmentReturnedOutlinedIcon from '@mui/icons-material/AssignmentReturnedOutlined'
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined'
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useSnackbar } from 'notistack'
import { apiConstants, getGeneric, putGeneric, postMultipart } from '../../common/constants'
import { formatCurrency, formatDate } from '../../common/util'
import { useGetAllActiveMerchantsIdAndNameCachedQuery } from '../../redux/commonApiCalls'
import PageTitle from '../../components/PageTitle'
import Loader from '../../common/Loader'

const STATUS_FLOW = ['PENDING', 'UNDER_REVIEW', 'DATA_UPLOADED', 'APPROVED', 'CLOSED']

// CT statuses meaning "additional documents have been requested from the merchant".
// Exact string pending CT confirmation — both variants handled defensively.
const MORE_DATA_STATUSES = ['MORE_DATA_REQUESTED', 'DOCUMENTS_PENDING']

const statusColor = (s) => {
  switch (s) {
    case 'PENDING':
    case 'DOCUMENTS_PENDING':
    case 'MORE_DATA_REQUESTED':
      return 'warning'
    case 'UNDER_REVIEW':
    case 'DATA_UPLOADED':
      return 'info'
    case 'APPROVED':
    case 'PARTIALLY_APPROVED':
      return 'success'
    case 'CLOSED':
      return 'default'
    case 'REJECTED':
      return 'error'
    default:
      return 'primary'
  }
}

const prettyStatus = (s) =>
  s ? s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : '—'

const fileIconFor = (name = '') => {
  const ext = name.split('.').pop()?.toLowerCase()
  if (['mp4', 'mov', 'avi', 'webm'].includes(ext)) return <VideocamOutlinedIcon fontSize="small" />
  if (['mp3', 'aac', 'ogg', 'wav', 'm4a'].includes(ext)) return <AudiotrackOutlinedIcon fontSize="small" />
  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(ext)) return <ImageOutlinedIcon fontSize="small" />
  if (ext === 'pdf') return <PictureAsPdfOutlinedIcon fontSize="small" />
  return <DescriptionOutlinedIcon fontSize="small" />
}

const Connector = styled(StepConnector)(({ theme }) => ({
  [`& .${stepConnectorClasses.line}`]: {
    borderColor: theme.palette.divider,
    borderTopWidth: 2,
  },
  [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}, &.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]:
    {
      borderColor: theme.palette.primary.main,
    },
}))

// Flat card matching Analytics page style
const flatCardSx = {
  borderRadius: 2.5,
  border: '1px solid #e8e8e8',
  boxShadow: 'none',
}

// Compact KPI tile, mirrors Analytics KpiCard
function KpiTile({ icon, label, value, color, bg }) {
  return (
    <Box
      sx={{
        borderRadius: 2.5,
        border: '1px solid #e8e8e8',
        backgroundColor: '#fff',
        p: 2,
        height: '100%',
        display: 'flex',
        gap: 1.5,
        alignItems: 'flex-start',
      }}
    >
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: 2,
          backgroundColor: bg,
          border: `1px solid ${color}25`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          color,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            color: '#9e9e9e',
            fontWeight: 600,
            fontSize: '0.68rem',
            textTransform: 'uppercase',
            letterSpacing: 0.5,
          }}
        >
          {label}
        </Typography>
        <Typography sx={{ fontWeight: 800, fontSize: '1.5rem', color: '#1a1a1a', lineHeight: 1.1, mt: 0.4 }}>
          {value}
        </Typography>
      </Box>
    </Box>
  )
}

const SectionHeader = ({ icon, title, count }) => (
  <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
    {icon && (
      <Box sx={{ display: 'flex', color: '#1565c0', fontSize: 18 }}>{icon}</Box>
    )}
    <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: '#1a1a1a' }}>
      {title}
    </Typography>
    {count != null && (
      <Chip
        label={count}
        size="small"
        sx={{ height: 18, fontSize: '0.68rem', fontWeight: 600, bgcolor: '#f0f4fa', color: '#1565c0' }}
      />
    )}
  </Stack>
)

const InfoRow = ({ label, value, mono, copyable }) => (
  <Stack
    direction="row"
    sx={{
      py: 0.9,
      borderBottom: '1px dashed #eee',
      '&:last-of-type': { borderBottom: 0 },
    }}
  >
    <Typography
      variant="body2"
      sx={{ minWidth: 130, flexShrink: 0, color: '#9e9e9e', fontSize: '0.78rem', fontWeight: 500 }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        fontFamily: mono ? 'monospace' : undefined,
        wordBreak: 'break-word',
        fontWeight: 600,
        color: '#1a1a1a',
        fontSize: '0.82rem',
        flex: 1,
      }}
    >
      {value ?? '—'}
    </Typography>
    {copyable && value ? <CopyIcon text={value} /> : null}
  </Stack>
)

function ClaimDetail() {
  const { claimId } = useParams()
  const navigate = useNavigate()
  const { enqueueSnackbar } = useSnackbar()
  const [claim, setClaim] = useState(null)
  const [loading, setLoading] = useState(false)
  const [reload, setReload] = useState(0)
  const [requestDataOpen, setRequestDataOpen] = useState(false)
  const [requestDataNote, setRequestDataNote] = useState('')
  const [reopenOpen, setReopenOpen] = useState(false)
  const [reopenReason, setReopenReason] = useState('')
  const [actionLoading, setActionLoading] = useState(false)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [uploadFiles, setUploadFiles] = useState([])
  const [uploadDescription, setUploadDescription] = useState('')
  const [uploadComment, setUploadComment] = useState('')
  const [uploading, setUploading] = useState(false)

  const { data: allMerchants = [] } = useGetAllActiveMerchantsIdAndNameCachedQuery()
  const merchantNameById = useMemo(() => {
    const map = {}
    allMerchants.forEach((m) => { map[m.id] = m.name })
    return map
  }, [allMerchants])

  useEffect(() => {
    if (!claimId) return
    setLoading(true)
    getGeneric(`${apiConstants.CLAIMS_DETAIL}/${claimId}`)
      .then((res) => {
        setClaim(res?.data?.responseObj || res?.data || null)
      })
      .catch((err) => {
        console.error(err)
        enqueueSnackbar('Failed to load claim', { variant: 'error' })
      })
      .finally(() => setLoading(false))
  }, [claimId, reload, enqueueSnackbar])

  const claimIdValue = claim?.id || claim?.claimId || claimId
  const merchantName =
    claim?.brandName ||
    claim?.merchantName ||
    (claim?.merchantId ? merchantNameById[claim.merchantId] : null) ||
    claim?.merchantId ||
    '—'
  const awb =
    claim?.awb ||
    claim?.awbNumber ||
    claim?.proshipOnlyData?.proshipAwbNumber ||
    claim?.orderData?.orderAwb ||
    ''
  const courier = claim?.courierId || claim?.courierCode || claim?.courierPartner
  const filedAt = claim?.claimRegisteredDate || claim?.filedAt
  const lastUpdatedAt = claim?.latestStatusUpdatedDate
  const submitByAt = claim?.claimSubmitByDate

  const supportingDocs = claim?.supportingDocuments || claim?.proofs || claim?.documents || []
  const statusHistory = claim?.claimStatusHistory || claim?.statusHistory || claim?.statusLog || []
  const modifiedHistory = claim?.modifiedHistory || []

  const currentStatus = claim?.claimStatus || claim?.status
  const isRejected = currentStatus === 'REJECTED'
  const needsMoreData = MORE_DATA_STATUSES.includes(currentStatus)

  const lastRejectedEntry = statusHistory.find((h) => h.status === 'REJECTED')
  const rejectionReason =
    claim?.rejectionReason ||
    lastRejectedEntry?.comment ||
    lastRejectedEntry?.notes ||
    null

  const filedBy =
    modifiedHistory[0]?.modifiedByEmail ||
    modifiedHistory[0]?.modifiedByMobile ||
    claim?.filedByUserId

  const lastMoreDataEntry = statusHistory.find((h) => MORE_DATA_STATUSES.includes(h.status))
  const moreDataNote = lastMoreDataEntry?.comment || lastMoreDataEntry?.notes || claim?.statusComment || null

  const activeStep = isRejected || needsMoreData
    ? STATUS_FLOW.indexOf('UNDER_REVIEW')
    : Math.max(STATUS_FLOW.indexOf(currentStatus), 0)

  const invoiceValue = claim?.invoiceValue
  const approvedAmount = claim?.amountApproved
  const rejectedAmount = claim?.amountRejected
  const pendingAmount =
    invoiceValue != null
      ? Math.max(0, invoiceValue - (approvedAmount || 0) - (rejectedAmount || 0))
      : null

  const handleRequestMoreData = async () => {
    if (!requestDataNote.trim()) {
      enqueueSnackbar('Please enter a note describing what data is needed', { variant: 'error' })
      return
    }
    setActionLoading(true)
    try {
      // TODO: Backend endpoint needed — PUT /api/claims/{id}/status
      await putGeneric(`${apiConstants.CLAIMS_DETAIL}/${claimIdValue}/status`, {
        status: 'MORE_DATA_REQUESTED',
        comment: requestDataNote.trim(),
      })
      enqueueSnackbar('More data requested — merchant will be notified', { variant: 'success' })
      setRequestDataOpen(false)
      setRequestDataNote('')
      setReload((r) => r + 1)
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || 'Failed to update status', { variant: 'error' })
    }
    setActionLoading(false)
  }

  const handleReopenClaim = async () => {
    if (!reopenReason.trim()) {
      enqueueSnackbar('Please provide a reason for reopening', { variant: 'error' })
      return
    }
    setActionLoading(true)
    try {
      // TODO: Backend endpoint needed — PUT /api/claims/{id}/reopen
      await putGeneric(`${apiConstants.CLAIMS_DETAIL}/${claimIdValue}/reopen`, {
        reason: reopenReason.trim(),
      })
      enqueueSnackbar('Claim reopened and moved to Under Review', { variant: 'success' })
      setReopenOpen(false)
      setReopenReason('')
      setReload((r) => r + 1)
    } catch (err) {
      enqueueSnackbar(err?.response?.data?.message || 'Failed to reopen claim', { variant: 'error' })
    }
    setActionLoading(false)
  }

  const copyId = () => {
    if (!claimIdValue) return
    navigator.clipboard?.writeText(claimIdValue)
    enqueueSnackbar('Claim ID copied', { variant: 'success' })
  }

  const closeUploadDialog = () => {
    setUploadOpen(false)
    setUploadFiles([])
    setUploadDescription('')
    setUploadComment('')
  }

  const handleUploadDocs = async () => {
    if (!uploadFiles.length) {
      enqueueSnackbar('Please select at least one file', { variant: 'error' })
      return
    }
    setUploading(true)
    let uploaded = 0
    try {
      for (const file of uploadFiles) {
        const form = new FormData()
        form.append('claimId', claimIdValue)
        form.append('fileDescription', uploadDescription.trim() || 'PROOF_DOCUMENT')
        form.append('file', file)
        if (uploadComment.trim()) form.append('comment', uploadComment.trim())
        // eslint-disable-next-line no-await-in-loop
        await postMultipart(apiConstants.CLAIMS_UPLOAD_EVIDENCE, form)
        uploaded += 1
      }
      enqueueSnackbar(`${uploaded} document${uploaded > 1 ? 's' : ''} uploaded successfully`, { variant: 'success' })
      closeUploadDialog()
      setReload((r) => r + 1)
    } catch (err) {
      console.error(err)
      enqueueSnackbar(
        err?.response?.data?.exception || err?.response?.data?.message ||
          (uploaded ? `Failed after uploading ${uploaded} file(s) — please retry the remaining` : 'Failed to upload document'),
        { variant: 'error' }
      )
    }
    setUploading(false)
  }

  const fileNameFromUrl = (url = '') => {
    try {
      const last = decodeURIComponent(url.split('?')[0].split('/').pop() || '')
      const m = last.match(/^[0-9a-f-]{36}_(.+)$/i)
      return m ? m[1] : last
    } catch {
      return url
    }
  }

  return (
    <Box sx={{ p: { xs: 2, md: 2.5 }, bgcolor: '#f9fafb', minHeight: '100vh' }}>
      {loading && <Loader />}
      <PageTitle>Claim Details</PageTitle>

      <Grid container spacing={2}>
        {/* ─── Header / Status Hero ─────────────────────────── */}
        <Grid item xs={12}>
          <Card sx={flatCardSx}>
            <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
              >
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
                  <IconButton
                    onClick={() => navigate(-1)}
                    size="small"
                    sx={{ border: '1px solid #e8e8e8' }}
                  >
                    <ArrowBackIosNewIcon fontSize="small" />
                  </IconButton>
                  <Box sx={{ minWidth: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Typography
                        sx={{
                          color: '#9e9e9e',
                          fontWeight: 700,
                          fontSize: '0.68rem',
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                        }}
                      >
                        Claim
                      </Typography>
                      <Chip
                        size="small"
                        color={statusColor(currentStatus)}
                        label={prettyStatus(currentStatus)}
                        sx={{ fontWeight: 600, height: 22 }}
                      />
                      {claim?.locked && (
                        <Chip
                          size="small"
                          icon={<LockOutlinedIcon sx={{ fontSize: 13 }} />}
                          variant="outlined"
                          color="warning"
                          label="Locked"
                          sx={{ height: 22 }}
                        />
                      )}
                      {claim?.nonE2E && (
                        <Chip size="small" variant="outlined" label="Non-E2E" sx={{ height: 22 }} />
                      )}
                      {claim?.claimSource && (
                        <Chip
                          size="small"
                          variant="outlined"
                          label={claim.claimSource.replace(/_/g, ' ')}
                          sx={{ height: 22 }}
                        />
                      )}
                    </Stack>
                    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.4 }}>
                      <Typography
                        sx={{
                          fontWeight: 800,
                          fontSize: '1.15rem',
                          fontFamily: 'monospace',
                          letterSpacing: -0.2,
                          color: '#1a1a1a',
                          wordBreak: 'break-all',
                        }}
                      >
                        {claimIdValue}
                      </Typography>
                      <Tooltip title="Copy Claim ID">
                        <IconButton size="small" onClick={copyId}>
                          <ContentCopyOutlinedIcon sx={{ fontSize: 14 }} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                    <Typography sx={{ mt: 0.3, color: '#6b6b6b', fontSize: '0.78rem' }}>
                      <strong>{merchantName}</strong> · AWB{' '}
                      <MuiLink
                        component={Link}
                        to={`/order-detail/${awb}`}
                        sx={{ fontFamily: 'monospace' }}
                      >
                        {awb || '—'}
                      </MuiLink>
                    </Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {/* Upload Documents — always available; highlighted when CT has requested more data */}
                  <Button
                    startIcon={<FileUploadOutlinedIcon />}
                    variant={needsMoreData ? 'contained' : 'outlined'}
                    color="warning"
                    size="small"
                    onClick={() => setUploadOpen(true)}
                    sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600 }}
                  >
                    Upload Documents
                  </Button>
                  {/* Request More Data — visible when Under Review or Data Uploaded */}
                  {['UNDER_REVIEW', 'DATA_UPLOADED', 'PENDING'].includes(currentStatus) && (
                    <Button
                      startIcon={<AssignmentReturnedOutlinedIcon />}
                      variant="contained"
                      color="warning"
                      size="small"
                      onClick={() => setRequestDataOpen(true)}
                      sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600 }}
                    >
                      Request More Data
                    </Button>
                  )}
                  {/* Reopen Claim — visible when Rejected or Closed */}
                  {['REJECTED', 'CLOSED'].includes(currentStatus) && (
                    <Button
                      startIcon={<ReplayOutlinedIcon />}
                      variant="contained"
                      color="info"
                      size="small"
                      onClick={() => setReopenOpen(true)}
                      sx={{ borderRadius: 1.5, textTransform: 'none', fontWeight: 600 }}
                    >
                      Reopen Claim
                    </Button>
                  )}
                  <Button
                    startIcon={<RefreshIcon />}
                    variant="outlined"
                    size="small"
                    onClick={() => setReload((r) => r + 1)}
                    sx={{ borderRadius: 1.5 }}
                  >
                    Refresh
                  </Button>
                </Stack>
              </Stack>

              <Box sx={{ mt: 2.5 }}>
                <Stepper activeStep={activeStep} alternativeLabel connector={<Connector />}>
                  {STATUS_FLOW.map((s) => (
                    <Step
                      key={s}
                      completed={!isRejected && STATUS_FLOW.indexOf(s) < activeStep}
                    >
                      <StepLabel
                        error={isRejected && s === 'UNDER_REVIEW'}
                        StepIconProps={{
                          sx:
                            isRejected && s === 'UNDER_REVIEW'
                              ? { color: 'error.main' }
                              : undefined,
                        }}
                      >
                        {prettyStatus(s)}
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>
                {isRejected && (
                  <Alert severity="error" variant="outlined" sx={{ mt: 1.5 }}>
                    <strong>Rejected.</strong> {rejectionReason || 'No reason provided.'}
                  </Alert>
                )}
                {needsMoreData && (
                  <Alert severity="warning" variant="outlined" sx={{ mt: 1.5 }}>
                    <strong>Additional documents requested from the merchant.</strong>{' '}
                    {moreDataNote || 'Awaiting additional proof documents on this claim.'}
                  </Alert>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* ─── Financial KPI Strip ─────────────────────────── */}
        <Grid item xs={6} md={3}>
          <KpiTile
            icon={<ReceiptLongOutlinedIcon fontSize="small" />}
            label="Invoice Value"
            value={invoiceValue != null ? formatCurrency(invoiceValue) : '—'}
            color="#1565c0"
            bg="#e3f2fd"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiTile
            icon={<CheckCircleOutlineIcon fontSize="small" />}
            label="Approved"
            value={approvedAmount != null ? formatCurrency(approvedAmount) : '—'}
            color="#2e7d32"
            bg="#e8f5e9"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiTile
            icon={<CancelOutlinedIcon fontSize="small" />}
            label="Rejected"
            value={rejectedAmount != null ? formatCurrency(rejectedAmount) : '—'}
            color="#c62828"
            bg="#ffebee"
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <KpiTile
            icon={<ScheduleOutlinedIcon fontSize="small" />}
            label="Pending"
            value={pendingAmount != null ? formatCurrency(pendingAmount) : '—'}
            color="#e65100"
            bg="#fff3e0"
          />
        </Grid>

        {/* ─── Shipment + Claim info (left) ─────────────────────────── */}
        <Grid item xs={12} md={7}>
          <Card sx={flatCardSx}>
            <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
              <SectionHeader
                icon={<DescriptionOutlinedIcon fontSize="inherit" />}
                title="Shipment & Claim Details"
              />

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Typography
                    sx={{
                      color: '#9e9e9e',
                      fontWeight: 700,
                      fontSize: '0.66rem',
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      mb: 0.5,
                    }}
                  >
                    Shipment
                  </Typography>
                  <InfoRow label="AWB" value={awb || '—'} mono copyable />
                  <InfoRow label="Order ID" value={claim?.orderData?.orderId || claim?.orderId} mono />
                  <InfoRow label="Courier" value={courier} />
                  <InfoRow label="Shipment Type" value={claim?.shipmentType} />
                  <InfoRow
                    label="Delivery Date"
                    value={
                      claim?.deliveryTimestamp
                        ? formatDate(claim.deliveryTimestamp, { format: 'dd MMM yyyy HH:mm' })
                        : '—'
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography
                    sx={{
                      color: '#9e9e9e',
                      fontWeight: 700,
                      fontSize: '0.66rem',
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      mb: 0.5,
                    }}
                  >
                    Claim
                  </Typography>
                  <InfoRow
                    label="Type"
                    value={claim?.claimType ? claim.claimType.replace(/_/g, ' ') : null}
                  />
                  <InfoRow label="Merchant" value={merchantName} />
                  <InfoRow label="Filed By" value={filedBy} />
                  <InfoRow
                    label="Filed At"
                    value={filedAt ? formatDate(filedAt, { format: 'dd MMM yyyy HH:mm' }) : '—'}
                  />
                  <InfoRow
                    label="Submit-By"
                    value={submitByAt ? formatDate(submitByAt, { format: 'dd MMM yyyy HH:mm' }) : '—'}
                  />
                  <InfoRow
                    label="Last Updated"
                    value={lastUpdatedAt ? formatDate(lastUpdatedAt, { format: 'dd MMM yyyy HH:mm' }) : '—'}
                  />
                  {claim?.statusComment && (
                    <InfoRow label="Status Note" value={claim.statusComment} />
                  )}
                </Grid>
              </Grid>

              {claim?.comment && (
                <Box
                  sx={{
                    mt: 2,
                    p: 1.5,
                    bgcolor: '#f7f9fc',
                    borderRadius: 1.5,
                    border: '1px solid #e8e8e8',
                  }}
                >
                  <Typography
                    sx={{
                      color: '#9e9e9e',
                      fontWeight: 700,
                      fontSize: '0.66rem',
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                    }}
                  >
                    Claimant Remarks
                  </Typography>
                  <Typography sx={{ mt: 0.4, fontSize: '0.82rem', color: '#1a1a1a', whiteSpace: 'pre-wrap' }}>
                    {claim.comment}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* ─── Supporting Documents (right) ─────────────────────────── */}
        <Grid item xs={12} md={5}>
          <Card sx={{ ...flatCardSx, height: '100%' }}>
            <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
              <SectionHeader
                icon={<DescriptionOutlinedIcon fontSize="inherit" />}
                title="Supporting Documents"
                count={supportingDocs.length}
              />
              {supportingDocs.length === 0 ? (
                <Box sx={{ py: 4, textAlign: 'center', color: '#9e9e9e' }}>
                  <DescriptionOutlinedIcon sx={{ fontSize: 32, opacity: 0.4 }} />
                  <Typography sx={{ fontSize: '0.78rem', mt: 0.5 }}>
                    No documents uploaded
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={1}>
                  {supportingDocs.map((p, idx) => {
                    const url = p.url || p.s3Url || p.signedUrl
                    const fileName = p.fileName || p.name || (url ? fileNameFromUrl(url) : null)
                    const hasFile = !!url
                    return (
                      <Paper
                        key={idx}
                        variant="outlined"
                        sx={{
                          p: 1.25,
                          borderRadius: 1.5,
                          borderColor: '#e8e8e8',
                          display: 'flex',
                          gap: 1.25,
                          alignItems: 'center',
                          transition: 'all 120ms ease',
                          '&:hover': hasFile
                            ? { borderColor: '#1565c0', bgcolor: '#fafbfd' }
                            : {},
                        }}
                      >
                        <Box
                          sx={{
                            width: 36,
                            height: 36,
                            borderRadius: 1.5,
                            bgcolor: '#e3f2fd',
                            color: '#1565c0',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                          }}
                        >
                          {fileIconFor(fileName)}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            sx={{ fontWeight: 600, fontSize: '0.82rem', color: '#1a1a1a' }}
                            noWrap
                            title={p.title}
                          >
                            {p.title || fileName || `Document ${idx + 1}`}
                          </Typography>
                          <Typography
                            sx={{
                              color: '#9e9e9e',
                              fontSize: '0.7rem',
                              display: 'block',
                              wordBreak: 'break-word',
                              mt: 0.2,
                            }}
                          >
                            {p.description ||
                              (hasFile
                                ? fileName
                                : p.documentNotAvailableRemark
                                ? `Not available: ${p.documentNotAvailableRemark}`
                                : '')}
                          </Typography>
                        </Box>
                        {hasFile && (
                          <Tooltip title="Open document">
                            <IconButton
                              component="a"
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              size="small"
                            >
                              <OpenInNewOutlinedIcon sx={{ fontSize: 16 }} />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Paper>
                    )
                  })}
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* ─── Activity Log (full width row, two columns inside) ─────────── */}
        <Grid item xs={12} md={modifiedHistory.length > 0 ? 7 : 12}>
          <Card sx={flatCardSx}>
            <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
              <SectionHeader
                icon={<ScheduleOutlinedIcon fontSize="inherit" />}
                title="Status Timeline"
                count={statusHistory.length}
              />
              {statusHistory.length === 0 ? (
                <Typography sx={{ color: '#9e9e9e', fontSize: '0.82rem' }}>
                  No status updates yet.
                </Typography>
              ) : (
                <Box sx={{ position: 'relative', pl: 3 }}>
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 11,
                      top: 6,
                      bottom: 6,
                      width: '2px',
                      bgcolor: '#e8e8e8',
                    }}
                  />
                  {[...statusHistory]
                    .sort((a, b) => {
                      const da = new Date(a.statusUpdatedDate || a.changedAt || a.date || 0).getTime()
                      const db = new Date(b.statusUpdatedDate || b.changedAt || b.date || 0).getTime()
                      return db - da
                    })
                    .map((h, idx) => {
                      const ts = h.statusUpdatedDate || h.changedAt || h.date
                      const actor = h.userEmail || h.userMobile || h.changedBy
                      const note = h.comment || h.notes || h.remark
                      return (
                        <Box key={idx} sx={{ position: 'relative', pb: idx === statusHistory.length - 1 ? 0 : 2 }}>
                          <Box
                            sx={{
                              position: 'absolute',
                              left: -16,
                              top: 4,
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor:
                                idx === 0 ? `${statusColor(h.status)}.main` : '#bdbdbd',
                              border: '2px solid #fff',
                              boxShadow: '0 0 0 1px #e8e8e8',
                            }}
                          />
                          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                            <Chip
                              size="small"
                              color={statusColor(h.status)}
                              label={prettyStatus(h.status)}
                              sx={{ fontWeight: 500, height: 20, fontSize: '0.7rem' }}
                            />
                            <Typography sx={{ color: '#9e9e9e', fontSize: '0.72rem' }}>
                              {ts ? formatDate(ts, { format: 'dd MMM yyyy HH:mm' }) : '—'}
                            </Typography>
                          </Stack>
                          {(actor || note) && (
                            <Typography sx={{ mt: 0.4, fontSize: '0.78rem', color: '#555' }}>
                              {actor ? <strong style={{ color: '#1a1a1a' }}>{actor}: </strong> : null}
                              {note}
                            </Typography>
                          )}
                        </Box>
                      )
                    })}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {modifiedHistory.length > 0 && (
          <Grid item xs={12} md={5}>
            <Card sx={{ ...flatCardSx, height: '100%' }}>
              <CardContent sx={{ py: 2, px: 2.5, '&:last-child': { pb: 2 } }}>
                <SectionHeader
                  icon={<HistoryEduOutlinedIcon fontSize="inherit" />}
                  title="Edit History"
                  count={modifiedHistory.length}
                />
                <Stack spacing={1}>
                  {[...modifiedHistory]
                    .sort((a, b) => new Date(b.modifiedDate || 0) - new Date(a.modifiedDate || 0))
                    .map((h, idx) => (
                      <Paper
                        key={idx}
                        variant="outlined"
                        sx={{ p: 1.25, borderRadius: 1.5, borderColor: '#e8e8e8' }}
                      >
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="baseline"
                          flexWrap="wrap"
                          gap={1}
                        >
                          <Typography sx={{ fontWeight: 600, fontSize: '0.78rem', color: '#1a1a1a' }}>
                            {h.modifiedByEmail || h.modifiedByMobile || '—'}
                          </Typography>
                          <Typography sx={{ color: '#9e9e9e', fontSize: '0.7rem' }}>
                            {h.modifiedDate
                              ? formatDate(h.modifiedDate, { format: 'dd MMM yyyy HH:mm' })
                              : '—'}
                          </Typography>
                        </Stack>
                        <Typography sx={{ mt: 0.4, fontSize: '0.76rem', color: '#666' }}>
                          {h.description || '—'}
                        </Typography>
                      </Paper>
                    ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* ─── Request More Data Dialog ────────────────── */}
      <Dialog open={requestDataOpen} onClose={() => setRequestDataOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <AssignmentReturnedOutlinedIcon color="warning" />
            <span>Request More Data</span>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            The merchant will be notified and can upload additional proof documents for this claim.
            An automated email will be sent to the merchant's registered email.
          </Typography>
          <MuiTextField
            fullWidth
            multiline
            rows={3}
            label="What additional data is needed?"
            placeholder="e.g., Please upload the unboxing video showing the AWB number clearly..."
            value={requestDataNote}
            onChange={(e) => setRequestDataNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRequestDataOpen(false)} variant="outlined" size="small">Cancel</Button>
          <Button onClick={handleRequestMoreData} variant="contained" color="warning" size="small"
            disabled={actionLoading || !requestDataNote.trim()}>
            {actionLoading ? 'Sending...' : 'Request Data'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Reopen Claim Dialog (Admin-only warning) ── */}
      <Dialog open={reopenOpen} onClose={() => setReopenOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <AdminPanelSettingsOutlinedIcon color="info" />
            <span>Reopen Claim</span>
          </Stack>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, borderRadius: 1.5 }}>
            <strong>Admin access required.</strong> Only administrators can reopen rejected or closed claims.
            This action will move the claim back to "Under Review" status and create an audit trail entry.
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Claim <strong>{claimIdValue}</strong> is currently <Chip size="small" color={statusColor(currentStatus)} label={prettyStatus(currentStatus)} sx={{ height: 20, mx: 0.5 }} />.
            Reopening will reset it to Under Review.
          </Typography>
          <MuiTextField
            fullWidth
            multiline
            rows={3}
            label="Reason for reopening *"
            placeholder="e.g., New evidence submitted by merchant, needs re-evaluation..."
            value={reopenReason}
            onChange={(e) => setReopenReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setReopenOpen(false)} variant="outlined" size="small">Cancel</Button>
          <Button onClick={handleReopenClaim} variant="contained" color="info" size="small"
            disabled={actionLoading || !reopenReason.trim()}>
            {actionLoading ? 'Reopening...' : 'Reopen Claim'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Upload Additional Documents Dialog ──────── */}
      <Dialog open={uploadOpen} onClose={() => !uploading && closeUploadDialog()} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <FileUploadOutlinedIcon color="warning" />
            <span>Upload Additional Documents</span>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {moreDataNote && (
            <Alert severity="info" sx={{ mb: 2, borderRadius: 1.5 }}>
              <strong>Requested:</strong> {moreDataNote}
            </Alert>
          )}
          <Button
            component="label"
            variant="outlined"
            fullWidth
            startIcon={<FileUploadOutlinedIcon />}
            sx={{ mb: 1.5, py: 1.5, borderStyle: 'dashed', textTransform: 'none' }}
          >
            {uploadFiles.length ? 'Add more files' : 'Select files'}
            <input
              hidden
              type="file"
              multiple
              accept="image/*,video/mp4,audio/*,.pdf"
              onChange={(e) => {
                const picked = Array.from(e.target.files || [])
                setUploadFiles((prev) => [...prev, ...picked])
                e.target.value = ''
              }}
            />
          </Button>
          {uploadFiles.length > 0 && (
            <Stack spacing={0.5} sx={{ mb: 1.5 }}>
              {uploadFiles.map((f, idx) => (
                <Stack key={`${f.name}-${idx}`} direction="row" alignItems="center" spacing={1}>
                  <Typography sx={{ fontSize: '0.78rem', flex: 1 }} noWrap>
                    {f.name}
                  </Typography>
                  <Typography sx={{ fontSize: '0.7rem', color: '#9e9e9e', flexShrink: 0 }}>
                    {(f.size / (1024 * 1024)).toFixed(1)} MB
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => setUploadFiles((prev) => prev.filter((_, i) => i !== idx))}
                  >
                    <CancelOutlinedIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          )}
          <MuiTextField
            fullWidth
            size="small"
            label="Document description"
            placeholder="e.g. UNBOXING_PROOF, WAREHOUSE_POD"
            value={uploadDescription}
            onChange={(e) => setUploadDescription(e.target.value)}
            sx={{ mb: 1.5 }}
          />
          <MuiTextField
            fullWidth
            size="small"
            multiline
            rows={2}
            label="Comment (optional)"
            value={uploadComment}
            onChange={(e) => setUploadComment(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeUploadDialog} variant="outlined" size="small" disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleUploadDocs}
            variant="contained"
            color="warning"
            size="small"
            disabled={uploading || !uploadFiles.length}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default ClaimDetail
